import { Vector2 } from 'three';
import { assertDefined, debugCommand } from 'shared/utils/debug';
import { computeImageMapSize, parseLodId } from './helpers';
function isLoaded(state) {
    return (state.lodData !== undefined &&
        state.mapPosition !== undefined &&
        state.contentMetrics !== undefined);
}
function isEqualExtraData(a, b) {
    return a.position.isEqual(b.position) && a.size.equals(b.size) && a.shading.equals(b.shading);
}
export default class ImageRegistry {
    constructor(tileMap, tileLoader) {
        this.tileMap = tileMap;
        this.tileLoader = tileLoader;
        this.state = new Map();
        this.loading = false;
        this.onImageReadyCallback = () => { };
        const tileCountRef = new WeakRef(() => {
            let tileCount = 0;
            this.state.forEach(data => {
                tileCount += data.lodData !== undefined ? computeImageMapSize(data.lodData) : 0;
            });
            return tileCount;
        });
        debugCommand('tileCount', () => {
            const func = tileCountRef.deref();
            if (func !== undefined)
                return func();
        });
    }
    isLoading() {
        return this.loading;
    }
    updateImages(images, lodLoader) {
        const updated = [];
        const staleIds = Array.from(this.state.keys()).filter(id => !images.has(id));
        staleIds.forEach(id => {
            this.disposeState(this.state.get(id));
            this.state.delete(id);
        });
        const newImages = Array.from(images.entries()).filter(([id, _]) => !this.state.has(id));
        newImages.forEach(([id, extraData]) => {
            this.state.set(id, { id, extraData });
        });
        images.forEach((extraData, id) => {
            const state = this.state.get(id);
            assertDefined(state, 'Updating unknown image');
            if (state.extraData !== undefined && !isEqualExtraData(extraData, state.extraData)) {
                updated.push(id);
                this.tileLoader.updateImage(id, extraData);
            }
            state.extraData = extraData;
        });
        if (newImages.length > 0) {
            const uniqueImageIds = Array.from(new Set(newImages.map(([_, it]) => it.lodId)));
            this.loading = true;
            lodLoader(uniqueImageIds).then(data => {
                data.forEach(lodData => {
                    this.state.forEach(state => {
                        const parsedLodId = parseLodId(state.extraData.lodId).id;
                        if (parsedLodId === lodData.id && !isLoaded(state)) {
                            this.finalizeState(state, lodData);
                            this.tileLoader.addImage(state);
                            this.onImageReadyCallback(state.id);
                        }
                    });
                });
                this.loading = false;
            });
        }
        return updated;
    }
    onImageReady(callback) {
        this.onImageReadyCallback = callback;
    }
    static computeContentMetrics(lodData) {
        const fullSize = new Vector2(...lodData.full_size);
        const fitSize = new Vector2(...lodData.fit_size);
        const ratio = fitSize.divide(fullSize);
        const offset = new Vector2(1.0, 1.0).sub(ratio).multiplyScalar(0.5);
        return { ratio, offset };
    }
    finalizeState(state, lodData) {
        state.lodData = lodData;
        state.mapPosition = this.tileMap.reserveSpace(computeImageMapSize(state.lodData));
        state.contentMetrics = ImageRegistry.computeContentMetrics(lodData);
    }
    disposeState(state) {
        if (isLoaded(state)) {
            this.tileLoader.removeImage(state);
            this.tileMap.releaseSpace(state.mapPosition, computeImageMapSize(state.lodData));
        }
    }
    getImageUniforms(id) {
        return this.stateToUniforms(this.state.get(id));
    }
    getContentRatio(id) {
        const state = this.state.get(id);
        if (state === undefined || !isLoaded(state))
            return undefined;
        return state.contentMetrics.ratio;
    }
    stateToUniforms(state) {
        if (state === undefined || !isLoaded(state))
            return undefined;
        const itemMapSize = computeImageMapSize(state.lodData);
        return {
            itemMapCoordinates: { value: state.mapPosition },
            itemMapSize: { value: itemMapSize },
            contentRatio: { value: state.contentMetrics.ratio },
            contentOffset: { value: state.contentMetrics.offset },
            shading: { value: state.extraData.shading },
            canvasUnitSize: { value: state.extraData.size },
            cornerRadius: { value: state.extraData.cornerRadius },
        };
    }
}
